/**
 * Layout store: the saved layout (server), the edit-mode draft, and the edit actions.
 *
 * `saved === null` means "use the role default preset". Edit mode works on a deep copy;
 * Tallenna PUTs it. Widget settings changed from view mode (the popover's own Tallenna,
 * or the sticky note's autosave) PUT immediately with rollback on failure.
 */
import { create } from 'zustand';

import { getLayout, putLayout, readLocalLayout, writeLocalLayout } from './layoutApi';
import { defaultPresetId, layoutFromPreset } from './presets';
import { WIDGET_BY_ID } from './registry';
import { nextSizeForGroup } from './sizes';
import { sanitizeLayout, visibleWidgets } from './visibility';

const clone = (layout) => (layout ? JSON.parse(JSON.stringify(layout)) : null);
const serialize = (layout) => JSON.stringify(layout?.widgets || []);

let loadToken = 0;

export const useDashboardStore = create((set, get) => ({
  orgId: null,
  simplified: false, // the UI mode the layout was loaded for (utils/access.js isSimplifiedUi)
  status: 'idle', // idle | loading | ready
  saved: null,
  draft: null,
  mode: 'view',
  saving: false,
  saveError: null,
  loadError: null,
  updatedAt: null,
  lastRemoved: null, // { widget, index, token }
  scrollToId: null,

  load: async (orgId, simplified) => {
    const token = ++loadToken;
    const local = readLocalLayout(orgId);
    set({
      orgId,
      simplified,
      saved: local ? sanitizeLayout(local) : null,
      status: local ? 'ready' : 'loading',
      draft: null,
      mode: 'view',
      loadError: null,
      lastRemoved: null,
    });
    try {
      const payload = await getLayout(orgId);
      if (token !== loadToken) return;
      const saved = payload?.layout ? sanitizeLayout(payload.layout) : null;
      writeLocalLayout(orgId, saved);
      set({ saved, status: 'ready', updatedAt: payload?.updated_at || null });
    } catch (error) {
      if (token !== loadToken) return;
      set({ status: 'ready', loadError: error });
    }
  },

  clear: () => {
    loadToken += 1;
    set({ orgId: null, simplified: false, status: 'idle', saved: null, draft: null, mode: 'view', lastRemoved: null });
  },

  enterEdit: (organization) => {
    if (get().mode === 'edit') return;
    set({ draft: clone(selectEffectiveLayout(get(), organization)), mode: 'edit', saveError: null, lastRemoved: null });
  },

  cancelEdit: () => set({ draft: null, mode: 'view', lastRemoved: null, saveError: null }),

  save: async () => {
    const { orgId, draft } = get();
    if (!orgId || !draft) return true;
    set({ saving: true, saveError: null });
    try {
      const payload = await putLayout(orgId, draft);
      const saved = payload?.layout ? sanitizeLayout(payload.layout) : null;
      writeLocalLayout(orgId, saved);
      set({ saved, draft: null, mode: 'view', saving: false, updatedAt: payload?.updated_at || null, lastRemoved: null });
      return true;
    } catch (error) {
      set({ saving: false, saveError: error });
      return false;
    }
  },

  moveById: (activeId, overId) => {
    const { draft } = get();
    if (!draft || activeId === overId) return;
    const widgets = draft.widgets.slice();
    const from = widgets.findIndex((widget) => widget.id === activeId);
    const to = widgets.findIndex((widget) => widget.id === overId);
    if (from === -1 || to === -1) return;
    const [moved] = widgets.splice(from, 1);
    widgets.splice(to, 0, moved);
    set({ draft: { ...draft, widgets } });
  },

  restoreOrder: (ids) => {
    const { draft } = get();
    if (!draft || !Array.isArray(ids)) return;
    const byId = Object.fromEntries(draft.widgets.map((widget) => [widget.id, widget]));
    const widgets = ids.map((id) => byId[id]).filter(Boolean);
    draft.widgets.forEach((widget) => {
      if (!ids.includes(widget.id)) widgets.push(widget);
    });
    set({ draft: { ...draft, widgets } });
  },

  moveBy: (id, delta) => {
    const { draft } = get();
    if (!draft) return;
    const widgets = draft.widgets.slice();
    const from = widgets.findIndex((widget) => widget.id === id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= widgets.length) return;
    const [moved] = widgets.splice(from, 1);
    widgets.splice(to, 0, moved);
    set({ draft: { ...draft, widgets } });
  },

  resizeGroup: (id, group) => {
    const { draft } = get();
    if (!draft) return;
    const widgets = draft.widgets.map((widget) => {
      if (widget.id !== id) return widget;
      const entry = WIDGET_BY_ID[widget.id];
      return { ...widget, size: nextSizeForGroup(entry, widget.size, group) };
    });
    set({ draft: { ...draft, widgets } });
  },

  setOpts: async (id, opts, organization) => {
    const { draft, mode, orgId } = get();
    if (mode === 'edit' && draft) {
      set({ draft: { ...draft, widgets: draft.widgets.map((w) => (w.id === id ? { ...w, opts } : w)) } });
      return true;
    }
    const previous = get().saved;
    const base = clone(selectEffectiveLayout(get(), organization));
    const next = { ...base, widgets: base.widgets.map((w) => (w.id === id ? { ...w, opts } : w)) };
    set({ saved: next });
    try {
      const payload = await putLayout(orgId, next);
      const saved = payload?.layout ? sanitizeLayout(payload.layout) : next;
      writeLocalLayout(orgId, saved);
      set({ saved, updatedAt: payload?.updated_at || null });
      return true;
    } catch (error) {
      set({ saved: previous, saveError: error });
      return false;
    }
  },

  remove: (id) => {
    const { draft } = get();
    if (!draft) return;
    const index = draft.widgets.findIndex((widget) => widget.id === id);
    if (index === -1) return;
    const widgets = draft.widgets.slice();
    const [widget] = widgets.splice(index, 1);
    set({ draft: { ...draft, widgets }, lastRemoved: { widget, index, token: Date.now() } });
  },

  undoRemove: () => {
    const { draft, lastRemoved } = get();
    if (!draft || !lastRemoved) return;
    const widgets = draft.widgets.slice();
    widgets.splice(Math.min(lastRemoved.index, widgets.length), 0, lastRemoved.widget);
    set({ draft: { ...draft, widgets }, lastRemoved: null });
  },

  clearUndo: () => set({ lastRemoved: null }),

  add: (id, size, opts = {}) => {
    const { draft } = get();
    const entry = WIDGET_BY_ID[id];
    if (!draft || !entry || draft.widgets.some((widget) => widget.id === id)) return;
    const token = entry.sizes.includes(size) ? size : entry.defaultSize;
    set({ draft: { ...draft, widgets: [...draft.widgets, { id, size: token, opts }] }, scrollToId: id });
  },

  clearScrollTo: () => set({ scrollToId: null }),

  applyPreset: (presetId, organization) => {
    const layout = layoutFromPreset(presetId, organization);
    set({ draft: layout, mode: 'edit', lastRemoved: null, saveError: null });
  },

  resetToClassic: (organization) => {
    get().applyPreset(defaultPresetId(organization), organization);
  },
}));

// ---- selectors (plain functions over the state) ----

export function selectEffectiveLayout(state, organization) {
  if (state.saved) return state.saved;
  return layoutFromPreset(defaultPresetId(organization, state.simplified), organization);
}

export function selectCurrentLayout(state, organization) {
  return state.mode === 'edit' && state.draft ? state.draft : selectEffectiveLayout(state, organization);
}

export function selectIsDirty(state, organization) {
  if (state.mode !== 'edit' || !state.draft) return false;
  return serialize(state.draft) !== serialize(selectEffectiveLayout(state, organization));
}

export function selectVisibleWidgets(state, organization) {
  return visibleWidgets(selectCurrentLayout(state, organization), organization);
}

export function selectCurrentPresetId(state, organization) {
  const layout = selectCurrentLayout(state, organization);
  return layout?.preset || (state.saved ? null : defaultPresetId(organization, state.simplified));
}
